// Story 8.7 (ADO-2016) — the one toast the app shows at a time (ADR-004: Zustand for
// client-side global state).
//
// A surface calls `showToast` after its action lands; the single `ToastHost` mounted at the
// composition root draws it (every toast in the same place, below the site header) and
// dismisses it after `TOAST_DURATION_MS`. Living outside the
// caller means the toast outlives it: a Previous Orders card can collapse, page away or
// unmount while its confirmation is still on screen. A second `showToast` replaces the first
// and restarts the timer, keyed on `id`, so a rapid run of actions leaves only the latest.
// An add-to-cart toast raised over a loaded basket names its `cartLine`, and the host keeps
// its quantity live while it is on screen.
//
// Callers: the Previous Orders re-add (Story 8.7) and every successful one-click add through
// `useAddConfirmation` (Story 7.9, ADO-1141), except the camera scanner, which shows its own
// copy inside its dialog.

import { create } from 'zustand';
import type { ToastKind } from '../design-system';

/** How long a toast stays on screen (ms). */
export const TOAST_DURATION_MS = 5000;

export interface ToastMessage {
  readonly title: string;
  readonly description: string;
  /** Story 7.9 (ADO-1141): `addedToCart` for the add-to-cart toast, which the Toast draws as
   *  its fixed-width pill. Leave it off for the default hugging, wrapping toast (the Story 8.7
   *  re-add toasts, whose order total must stay whole). Both sit in the same place. */
  readonly kind?: ToastKind;
  /** The cart line an add-to-cart toast is about. While the toast is on screen its
   *  description follows that line's live quantity, so a stepper edit after the add updates
   *  it (see `ToastHost`). Leave it off for a toast whose text is fixed. */
  readonly cartLine?: ToastCartLine;
}

/** The cart line an add-to-cart toast follows. The toast shows `Qty {n} · {productName}`,
 *  where `n` is the line's live quantity less `quantityBeforeAdd`: the quantity the add
 *  carried at first, then moved by each later edit of the line. */
export interface ToastCartLine {
  readonly skuCode: string;
  readonly productName: string;
  /** What the line held before the add the toast confirms (0 for a new line). */
  readonly quantityBeforeAdd: number;
}

/** The add-to-cart toast's description for `quantity` of `productName`. */
export function addedToCartDescription(quantity: number, productName: string): string {
  return `Qty ${quantity} · ${productName}`;
}

export interface ShownToast extends ToastMessage {
  /** Bumped on every `showToast`, so the host restarts its timer for a replacement. */
  readonly id: number;
}

interface ToastStoreState {
  toast: ShownToast | null;
  showToast: (message: ToastMessage) => void;
  /** Dismiss toast `id`; a stale timer for a toast already replaced does nothing. */
  dismissToast: (id: number) => void;
  /** Test/session hygiene — drop the toast. */
  reset: () => void;
}

let nextId = 0;

export const useToastStore = create<ToastStoreState>()((set, get) => ({
  toast: null,
  showToast(message) {
    nextId += 1;
    set({ toast: { ...message, id: nextId } });
  },
  dismissToast(id) {
    if (get().toast?.id === id) set({ toast: null });
  },
  reset() {
    set({ toast: null });
  },
}));
